import { useState, useRef, useEffect } from 'react';
import {
    getLearningProgramPreviewService,
    getLearningProgramPreviewStatusService,
    getLearningProgramStatusService,
} from '../../../../services/learning_programs.service';
import { CHAT_TYPE, DQ_PRO_PLAN_KEY } from '../../../../utils/constants';

export const useProgramBuilder = (setChatHistory, setModules, setProgramData, setOpenModules, setOpenUnits, onOutlineReady, onContentReady, onBuildServiceCall, currentPlanKey) => {
    const [isProgramBuilding, setIsProgramBuilding] = useState(false);
    const [step1Progress, setStep1Progress] = useState(0);
    const [contentProgress, setContentProgress] = useState(0);
    const [buildProgressStatus, setBuildProgressStatus] = useState(0);
    const isBuildingRef  = useRef(false);
    // Incremented every time a new build/resume starts.
    // Each polling loop captures the generation at start and exits if it changes.
    const buildGenRef = useRef(0);
    // Track the current plan key so polling loops can apply free-plan logic.
    const planKeyRef = useRef(currentPlanKey);
    useEffect(() => { planKeyRef.current = currentPlanKey; }, [currentPlanKey]);

    /**
     * Cancels any in-progress polling immediately.
     * Call this before starting a new session / on component unmount.
     */
    const cancelBuild = () => {
        buildGenRef.current += 1;   // invalidates all running tick loops
        isBuildingRef.current = false;
        setIsProgramBuilding(false);
        setBuildProgressStatus(0);
    };

    // ── Shared: load preview data and populate modules ──
    const loadPreviewData = async (code) => {
        try {
            const previewRes = await getLearningProgramPreviewService(code);
            if (previewRes?.data) {
                setProgramData(previewRes.data);
                const langKeys = Object.keys(previewRes.data.program_layout_previews || {});
                const firstLang = langKeys[0] || 'en-US';
                const preview = previewRes.data.program_layout_previews[firstLang]?.program_layout_preview;
                if (preview?.modules) {
                    setModules(preview.modules);
                    setOpenModules({ 0: true });
                    setOpenUnits({ '0-0': true });
                }
            }
        } catch (_) {}
    };

    const pollOutlineStatus = (code) => new Promise((resolve) => {
        const gen = buildGenRef.current;
        let lastPercent = -1;
        // progress_status has been observed reading -1 transiently for an
        // outline that was genuinely still building normally (confirmed fine
        // moments later) — so one -1 read alone doesn't mean failure. Only
        // treat it as failed once it reads -1 on two consecutive ticks in a
        // row (a real stall, not a momentary blip).
        let consecutiveFailedReads = 0;

        const tick = async () => {
            if (buildGenRef.current !== gen) { resolve(false); return; } // cancelled
            try {
                const res = await getLearningProgramPreviewStatusService(code);
                if (buildGenRef.current !== gen) { resolve(false); return; } // cancelled mid-request

                if (res?.data?.ml_server_resp?.error) {
                    resolve(false);
                    return;
                }

                const isReady = res?.data?.is_outline_ready;
                const initiated = res?.data?.generation_initiated;
                const progressStatus = res?.data?.progress_status;
                if (progressStatus === -1) {
                    consecutiveFailedReads++;
                    if (consecutiveFailedReads >= 2) { resolve(false); return; }
                    setTimeout(tick, 5000);
                    return;
                }
                consecutiveFailedReads = 0;
                // Update raw status so the UI can map it directly to card states
                if (progressStatus != null) setBuildProgressStatus(progressStatus);
                const isOutlineComplete = progressStatus === 7 || (isReady && initiated);
                const pct = isOutlineComplete ? 100 : Math.round(((progressStatus || 0) / 7) * 100);
                if (pct !== lastPercent) { lastPercent = pct; setStep1Progress(pct); }
                if (isOutlineComplete) {
                    setStep1Progress(100);
                    resolve(true);
                    return;
                }
            } catch (_) {
                // Error (e.g. 404 deleted program) — stop polling; invokeApi already toasted.
                resolve(false);
                return;
            }
            setTimeout(tick, 5000);
        };
        tick();
    });

    // ── Shared: poll content until complete ──
    // Resolves true on success, false on cancellation/error/failure. An
    // explicit status of 'error' fails immediately. A per-session
    // ProgressStatus of -1 has also been observed for a session that was
    // genuinely still generating normally (confirmed fine moments later), so
    // it isn't trusted as an instant failure signal on its own — only once it
    // reads -1 on two consecutive ticks in a row (a real stall, not a
    // momentary blip). Without this a truly stuck session's percentage would
    // just never reach 100, so polling (and the "still building" UI) would
    // continue forever instead of eventually surfacing the failure.
    const pollContentStatus = (code) => new Promise((resolve) => {
        const gen = buildGenRef.current;
        let lastPercent = -1;
        let consecutiveFailedReads = 0;

        const tick = async () => {
            if (buildGenRef.current !== gen) { resolve(false); return; } // cancelled
            try {
                const contentRes = await getLearningProgramStatusService(code);
                if (buildGenRef.current !== gen) { resolve(false); return; } // cancelled mid-request
                const statusData = contentRes?.data?.contentStatus || {};
                const sessionKeys = Object.keys(statusData);
                const hasExplicitError = sessionKeys.some(k => {
                    const v = statusData[k];
                    const st = typeof v === 'object' ? v.status : v;
                    return st === 'error';
                });
                if (hasExplicitError) { resolve(false); return; }
                const hasNegativeSession = sessionKeys.some(k => {
                    const v = statusData[k];
                    const ps = typeof v === 'object' ? v.ProgressStatus : null;
                    return ps === -1;
                });
                if (hasNegativeSession) {
                    consecutiveFailedReads++;
                    if (consecutiveFailedReads >= 2) { resolve(false); return; }
                    setTimeout(tick, 10000);
                    return;
                }
                consecutiveFailedReads = 0;
                let pct = 0;
                if (sessionKeys.length > 0) {
                    let done = 0, partial = 0;
                    sessionKeys.forEach(k => {
                        const v = statusData[k];
                        const st = typeof v === 'object' ? v.status : v;
                        const ps = typeof v === 'object' ? v.ProgressStatus : null;
                        if (st === 'done' || ps === 7) done++;
                        else if (st === 'in_progress' && ps) partial = ps / 7;
                    });
                    // For free/default plan users the server only builds the first session.
                    // If at least 1 session is done, treat the full content as complete so
                    // the progress bar reaches 100% even when NumSessions > 1.
                    const isFreePlan = planKeyRef.current === DQ_PRO_PLAN_KEY.DEFAULT;
                    if (isFreePlan && done >= 1) {
                        pct = 100;
                    } else {
                        pct = Math.round(((done + partial) / sessionKeys.length) * 100);
                    }
                }
                if (pct !== lastPercent) { lastPercent = pct; setContentProgress(pct); }
                if (pct >= 100) { setContentProgress(100); resolve(true); return; }
            } catch (_) {
                // Error (e.g. 404 deleted program) — stop polling; invokeApi already toasted.
                resolve(false);
                return;
            }
            setTimeout(tick, 10000);
        };
        tick();
    });

    // ── Shared: run content phase → then push final chat message ──
    // Returns false (and pushes the same build_failed bubble pollOutlineStatus's
    // callers use) when content generation failed, so callers can skip marking
    // the build complete.
    const runContentPhase = async (code) => {
        setContentProgress(0);
        const contentOk = await pollContentStatus(code);
        if (!contentOk) {
            setChatHistory(prev => [...prev, { type: CHAT_TYPE.BOT, text: '', subType: 'build_failed', phase: 'content' }]);
            return false;
        }
        await loadPreviewData(code);
        if (onContentReady) onContentReady();
        return true;
    };

    /**
     * Called by the chat hook once the server reports the build has started.
     * Build, description, and content-generation are all handled server-side;
     * this function only polls for outline and content status.
     *
     * @param {string} programCode – program code from {{program_code=…}} server marker
     */
    const handleBuildProgram = async (programCode) => {
        // Server explicitly confirmed this build — cancel any stale polling (e.g. a
        // resumeBuild loop that was stuck on a credit-failed program) and start fresh.
        // Do NOT gate on isBuildingRef: that lock may be held by a stale resumeBuild.
        buildGenRef.current += 1;   // invalidates all running poll loops immediately
        isBuildingRef.current = true;
        setIsProgramBuilding(true);
        setStep1Progress(0);
        setContentProgress(0);
        setBuildProgressStatus(0);

        try {
            // Step 1: poll outline (server already initiated build)
            const outlineCompleted = await pollOutlineStatus(programCode);

            if (!outlineCompleted) {
                setChatHistory(prev => [...prev, { type: CHAT_TYPE.BOT, text: '', subType: 'build_failed', phase: 'outline' }]);
                setIsProgramBuilding(false);
                return;
            }

            await loadPreviewData(programCode);
            if (onOutlineReady) onOutlineReady();
            setIsProgramBuilding(false);

            // Step 2 → Final
            // Content generation was triggered server-side; we only poll here.
            const contentOk = await runContentPhase(programCode);
            if (!contentOk) return;

            // All phases complete → mark step 17
            if (onBuildServiceCall) onBuildServiceCall();
            setChatHistory(prev => [...prev, { type: CHAT_TYPE.BOT, text: '', formStep: 17, isActive: true }]);

        } catch (e) {
            console.error('[handleBuildProgram] error:', e);
            setIsProgramBuilding(false);
        } finally {
            isBuildingRef.current = false;
        }
    };

    /**
     * Resume an in-progress build after restoring saved state.
     * Checks current outline/content status and picks up polling from wherever it left off.
     * Build, description, and content-generation are server-side only;
     * this function never calls those services directly.
     *
     * @param {string}  programId
     * @param {boolean} buildTriggered – pass false (default) to skip when build was never started
     */
    const resumeBuild = async (programId, buildTriggered = false) => {
        if (!buildTriggered) return;
        if (isBuildingRef.current) return;
        isBuildingRef.current = true;
        buildGenRef.current += 1;   // cancel any previous polling loop

        try {
            const statusRes = await getLearningProgramPreviewStatusService(programId);
            const isReady        = statusRes?.data?.is_outline_ready;
            const initiated      = statusRes?.data?.generation_initiated;
            const progressStatus = statusRes?.data?.progress_status;
            const isOutlineComplete = progressStatus === 7 || (isReady && initiated);

            // Build has not been initiated on the server yet — nothing to resume.
            // This guards against running resume logic when the user refreshes before
            // they've actually triggered program creation.
            // Note: generation_initiated is only true after content gen starts (post-outline),
            // so we also check progressStatus — any value > 0 means outline is in progress.
            if (!initiated && !isOutlineComplete && !progressStatus) {
                return;
            }

            if (!isOutlineComplete) {
                // ── Outline still building → resume from Step 1 ──
                setIsProgramBuilding(true);
                setStep1Progress(0);
                setContentProgress(0);
                const outlineDone = await pollOutlineStatus(programId);

                if (!outlineDone) {
                    setChatHistory(prev => [...prev, { type: CHAT_TYPE.BOT, text: '', subType: 'build_failed', phase: 'outline' }]);
                    setIsProgramBuilding(false);
                    return;
                }

                await loadPreviewData(programId);
                if (onOutlineReady) onOutlineReady();
                setIsProgramBuilding(false);

                // Content generation was triggered server-side; just poll.
                const contentOk = await runContentPhase(programId);
                if (!contentOk) return;

                if (onBuildServiceCall) onBuildServiceCall();
                setChatHistory(prev => [...prev, { type: CHAT_TYPE.BOT, text: '', formStep: 17, isActive: true }]);
                return;
            }

            // ── Outline is done ──
            await loadPreviewData(programId);

            // Check if content is already finished
            let contentDone = false;
            try {
                const contentRes = await getLearningProgramStatusService(programId);
                const statusData  = contentRes?.data?.contentStatus || {};
                const sessionKeys = Object.keys(statusData);
                if (sessionKeys.length > 0) {
                    const isFreePlan = planKeyRef.current === DQ_PRO_PLAN_KEY.DEFAULT;
                    let doneCount = 0;
                    const allDone = sessionKeys.every(k => {
                        const v  = statusData[k];
                        const st = typeof v === 'object' ? v.status : v;
                        const ps = typeof v === 'object' ? v.ProgressStatus : null;
                        const sessionDone = st === 'done' || ps === 7;
                        if (sessionDone) doneCount++;
                        return sessionDone;
                    });
                    // For free/default plan users the server only builds the first session.
                    // Treat as fully done if at least 1 session is complete.
                    contentDone = isFreePlan ? doneCount >= 1 : allDone;
                }
            } catch (_) {}

            if (contentDone) {
                // Everything already finished on the server.
                // BUT if the user left the page before the build completed, formStep 17 was never
                // pushed to chatHistory (that only happens while the page is mounted).
                // Use the functional updater to atomically check + fix in one pass.
                setStep1Progress(100);
                setContentProgress(100);
                let wasAlreadyMarked = false;
                setChatHistory(prev => {
                    if (prev.some(m => m.formStep === 17)) {
                        wasAlreadyMarked = true;
                        return prev; // already complete, nothing to add
                    }
                    // User left while building — inject the missing completion entries now
                    return [
                        ...prev,
                        { type: CHAT_TYPE.BOT, text: '', formStep: 17, isActive: true },
                    ];
                });
                // Fire callbacks only when this is a newly-detected completion
                // (i.e. formStep 17 was not in the restored history)
                if (!wasAlreadyMarked) {
                    if (onBuildServiceCall) onBuildServiceCall();
                    if (onContentReady) onContentReady();
                }
                return;
            }

            // ── Content in progress (or server background task hasn't fired yet) → poll ──
            setStep1Progress(100);
            const contentOk = await runContentPhase(programId);
            if (!contentOk) return;

            if (onBuildServiceCall) onBuildServiceCall();
            setChatHistory(prev => [...prev, { type: CHAT_TYPE.BOT, text: '', formStep: 17, isActive: true }]);

        } catch (e) {
            console.error('[resumeBuild] error:', e);
        } finally {
            isBuildingRef.current = false;
        }
    };

    return { isProgramBuilding, step1Progress, contentProgress, buildProgressStatus, handleBuildProgram, resumeBuild, cancelBuild };
};
